import {
  Col,
  Row,
  Space,
  notification,
  Dropdown,
  Menu,
  Pagination,
} from 'antd';
import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { formatDate } from '../../utils/helpers';
import MobHeader from '../../Components/Header/MobHeader';
import StatusTag from '../../Components/StatusTag';
import {
  ThemeFilterBtn,
  ThemeButton,
  ThemeTableCard,
  ThemeTable,
  ThemeInput,
  ThemeMainWrapper,
  ThemeModal,
  CardPaginationWrapper,
} from '../Styles/Theme';
import { ThemeProjectTabWrapper } from '../Projects/ProjectsStyle';
import {
  AIJDVCard,
  AIJDVCardInner,
  AIJDVCardHeader,
  AIJDVCardTitle,
  AIJDVCardDuration,
  AIJDVCardFooter,
  AIJDVCardDate,
  AIJDVCardMenuIcon,
  AIJDVTableActionWrapper,
} from './PollAgentsStyle';
import Squares from '../Dashboard/Squares';
import { GreetingText } from '../Dashboard/DashboardStyle';
import { SearchOutlined, RightOutlined } from '@ant-design/icons';
import { Link } from 'react-router-dom';
import {
  getPollingAgentsApi,
  getPollingAgentsReset,
  deletePollingAgentApi,
  deletePollingAgentReset,
} from '../../store/actions/pollingAgentAction';
import { usePollingAgentPermissions } from '../../utils/permissions';
import useFolders from "../../hooks/useFolders";
import {
  FolderBackNav,
  FolderCardsSection,
  ItemsSectionLabel,
  NewFolderButton,
  FolderModals,
} from "../../Components/FolderComponents";

// Helper function to map backend status to display status
const mapStatusToDisplay = status => {
  const statusMap = {
    draft: 'Draft',
    ready: 'Ready',
    in_progress: 'In-Progress',
    paused: 'Paused',
    completed: 'Completed',
  };
  return statusMap[status] || status;
};

const PollAgents = props => {
  const {
    getPollingAgents,
    getPollingAgentsReset,
    getPollingAgentsState,
    deletePollingAgent,
    deletePollingAgentReset,
    deletePollingAgentState,
  } = props;

  const navigate = useNavigate();

  // Permission check - shortcut hook
  const { can } = usePollingAgentPermissions();

  const [activeView, setActiveView] = useState('card');
  const [tableData, setTableData] = useState([]);
  const [cardData, setCardData] = useState([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [deleteModalVisible, setDeleteModalVisible] = useState(false);

  const defaultFilters = {
    page: 1,
    limit: 12,
    search: '',
    status: '',
    sortBy: 'createdAt',
    sortDirection: 'desc',
  };

  const getFiltersFromUrl = () => {
    const urlParams = new URLSearchParams(window.location.search);
    const urlFilters = {};
    for (const [key, value] of urlParams.entries()) {
      urlFilters[key] = value;
    }
    const filters = {
      ...defaultFilters,
      ...urlFilters,
    };
    return filters;
  };

  const initialFilters = getFiltersFromUrl();
  const [filters, setFilters] = useState(initialFilters);
  const [searchValue, setSearchValue] = useState(initialFilters.search || '');

  // Shared folder hook
  const folderHook = useFolders({ entityType: 'polling_agent', searchValue });
  const {
    folders,
    filteredFolders,
    currentFolderId,
    currentFolderName,
    refreshNeeded,
    createFolderModal,
    setCreateFolderModal,
    renameFolderModal,
    setRenameFolderModal,
    folderToRename,
    deleteFolderModal,
    setDeleteFolderModal,
    folderToDelete,
    moveToFolderModal,
    setMoveToFolderModal,
    selectedFolderId,
    setSelectedFolderId,
    removeFromFolderModal,
    setRemoveFromFolderModal,
    itemToRemove: agentToRemove,
    foldersCollapsed,
    setFoldersCollapsed,
    itemsCollapsed: agentsCollapsed,
    setItemsCollapsed: setAgentsCollapsed,
    draggedItem: draggedAgent,
    dragOverFolderId,
    folderForm,
    renameFolderForm,
    createFolderState,
    renameFolderState,
    deleteFolderState,
    addItemToFolderState,
    removeItemFromFolderState,
    fetchFolders,
    getFoldersState,
    handleGetFoldersResponse,
    handleCreateFolderResponse,
    handleRenameFolderResponse,
    handleDeleteFolderResponse,
    handleAddItemToFolderResponse,
    handleRemoveItemFromFolderResponse,
    handleCreateFolder,
    handleRenameFolder,
    handleDeleteFolder,
    handleMoveToFolder,
    handleRemoveFromFolder,
    confirmRemoveFromFolder,
    handleFolderClick,
    handleBackToRoot,
    openRenameFolderModal,
    openDeleteFolderModal,
    openMoveToFolderModal,
    handleDragStart,
    handleDragEnd,
    handleFolderDragOver,
    handleFolderDragEnter,
    handleFolderDragLeave,
    handleFolderDrop,
  } = folderHook;

  const [isMobile, setIsMobile] = useState(false);
  useEffect(() => {
    const checkIsMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    checkIsMobile();
    window.addEventListener('resize', checkIsMobile);
    return () => {
      window.removeEventListener('resize', checkIsMobile);
    };
  }, []);

  // Handle URL-based pagination and filters
  useEffect(() => {
    const updatedFilters = getFiltersFromUrl();
    setFilters({ ...updatedFilters });
    setSearchValue(updatedFilters.search || '');
    if (currentFolderId) {
      getPollingAgents({ ...updatedFilters, folderId: currentFolderId });
    } else {
      getPollingAgents({ ...updatedFilters, excludeFoldered: true });
    }
  }, [window.location.search, currentFolderId]);

  // Handle polling agents response
  useEffect(() => {
    if (
      getPollingAgentsState.apiState === 'success' &&
      getPollingAgentsState.data
    ) {
      const agentsData =
        getPollingAgentsState.data.data || getPollingAgentsState.data;
      const agents = agentsData.polling_agents || agentsData.agents || [];


      if (agents && agents.length > 0) {
        setTotalRecords(agentsData.pagination?.total || agentsData.total_count || 0);

        const updatedAgents = agents.map((item) => ({
          key: item._id,
          id: item._id,
          name: item.agent_name || item.name || '-',
          agentName: item.agent_name || item.name || '-',
          agentId: item.agent_id || '-',
          maxDuration: `${item.max_duration_seconds || 3600} Sec`,
          createdOn: formatDate(item.createdAt),
          status: mapStatusToDisplay(item.poll_status || 'draft'),
          pollStatus: item.poll_status || 'draft',
        }));

        setTableData(updatedAgents);
        setCardData(updatedAgents);
      } else {
        setTableData([]);
        setCardData([]);
        setTotalRecords(0);
      }
      getPollingAgentsReset();
    } else if (getPollingAgentsState.apiState === 'error') {
      notification.error({
        message: 'Error',
        description:
          getPollingAgentsState.message || 'Failed to fetch AI JDV agents',
      });
      getPollingAgentsReset();
    }
  }, [getPollingAgentsState, getPollingAgentsReset]);

  // Handle delete response
  useEffect(() => {
    if (deletePollingAgentState.apiState === 'success') {
      notification.success({
        message: 'Success',
        description:
          deletePollingAgentState.message ||
          'AI JDV agent deleted successfully',
      });
      setDeleteModalVisible(false);
      setSelectedAgent(null);
      refreshAgents();
    } else if (deletePollingAgentState.apiState === 'error') {
      notification.error({
        message: 'Error',
        description:
          deletePollingAgentState.message || 'Failed to delete AI JDV agent',
      });
    }
    deletePollingAgentReset();
  }, [deletePollingAgentState, deletePollingAgentReset, getPollingAgents]);

  // Helper to re-fetch agents with current folder context
  const refreshAgents = () => {
    const updatedFilters = getFiltersFromUrl();
    if (currentFolderId) {
      getPollingAgents({ ...updatedFilters, folderId: currentFolderId });
    } else {
      getPollingAgents({ ...updatedFilters, excludeFoldered: true });
    }
  };

  // Folder hook: fetch on mount
  useEffect(() => {
    fetchFolders();
  }, []);

  // Folder hook: response handlers
  useEffect(() => { handleGetFoldersResponse(); }, [getFoldersState]);
  useEffect(() => { handleCreateFolderResponse(); }, [createFolderState]);
  useEffect(() => { handleRenameFolderResponse(); }, [renameFolderState]);
  useEffect(() => { handleDeleteFolderResponse(); }, [deleteFolderState]);
  useEffect(() => { handleAddItemToFolderResponse(); }, [addItemToFolderState]);
  useEffect(() => { handleRemoveItemFromFolderResponse(); }, [removeItemFromFolderState]);

  // Re-fetch agents when folder hook signals a refresh (after folder operations)
  useEffect(() => {
    if (refreshNeeded > 0) {
      refreshAgents();
    }
  }, [refreshNeeded]);

  // Build agent detail URL with folder context for back navigation
  const getAgentDetailUrl = (agentId) => {
    if (currentFolderId) {
      return `/polling-agents/polling-agent/create-polling-agent/${agentId}?fromFolderId=${currentFolderId}&fromFolderName=${encodeURIComponent(currentFolderName)}`;
    }
    return `/polling-agents/polling-agent/create-polling-agent/${agentId}`;
  };

  // Build create agent URL with folder context (so new agent gets added to folder)
  const getCreateAgentUrl = () => {
    if (currentFolderId) {
      return `/polling-agents/polling-agent/create-polling-agent?folderId=${currentFolderId}&folderName=${encodeURIComponent(currentFolderName)}`;
    }
    return '/polling-agents/polling-agent/create-polling-agent';
  };

  const handleSearch = value => {
    const queryParams = new URLSearchParams(window.location.search);
    if (value) {
      queryParams.set('search', value);
    } else {
      queryParams.delete('search');
    }
    queryParams.set('page', '1');
    const newUrl = `${window.location.pathname}?${queryParams.toString()}`;
    window.history.pushState({}, '', newUrl);
    setFilters({ ...filters, search: value, page: 1 });
    setSearchValue(value);
  };

  const handleTableChange = (pagination, _, sorter) => {
    const queryParams = new URLSearchParams(window.location.search);
    queryParams.set('page', pagination.current || 1);
    queryParams.set('limit', pagination.pageSize || 12);

    if (sorter && Object.keys(sorter).length > 0) {
      queryParams.set('sortBy', sorter.field || 'createdAt');
      queryParams.set(
        'sortDirection',
        sorter.order === 'ascend' ? 'asc' : 'desc'
      );
    } else {
      queryParams.set('sortBy', 'createdAt');
      queryParams.set('sortDirection', 'desc');
    }

    const newUrl = `${window.location.pathname}?${queryParams.toString()}`;
    window.history.pushState({}, '', newUrl);
    setFilters({
      ...filters,
      sortBy: sorter?.field || 'createdAt',
      sortDirection: sorter?.order === 'ascend' ? 'asc' : 'desc',
      page: pagination.current || 1,
      limit: pagination.pageSize || 12,
    });
  };

  const handleEdit = record => {
    navigate(getAgentDetailUrl(record.id));
  };

  const handleDelete = record => {
    setSelectedAgent(record);
    setDeleteModalVisible(true);
  };

  const confirmDelete = () => {
    if (selectedAgent) {
      deletePollingAgent({ id: selectedAgent.id });
    }
  };

  const renderCardMenu = record => {
    return (
      <Menu
        selectable={false}
        onClick={({ key, domEvent }) => {
          domEvent.stopPropagation();
          if (key === 'edit') {
            handleEdit(record);
          } else if (key === "moveToFolder") {
            openMoveToFolderModal(record);
          } else if (key === "removeFromFolder") {
            handleRemoveFromFolder(record);
          } else if (key === 'delete') {
            handleDelete(record);
          }
        }}
      >
        {can('edit') && <Menu.Item key="edit">Edit</Menu.Item>}
        <Menu.Item key="moveToFolder">Move to Folder</Menu.Item>
        {currentFolderId && (
          <Menu.Item key="removeFromFolder">Remove from Folder</Menu.Item>
        )}
        {can('delete') && (
          <>
            <Menu.Divider />
            <Menu.Item key="delete">Delete</Menu.Item>
          </>
        )}
      </Menu>
    );
  };

  const columns = [
    {
      title: 'Agent Name',
      dataIndex: 'agentName',
      key: 'agentName',
      render: text => <span>{text}</span>,
    },
    {
      title: 'Max Duration Seconds',
      dataIndex: 'maxDuration',
      key: 'maxDuration',
      render: text => <span>{text}</span>,
    },
    {
      title: 'Created On',
      dataIndex: 'createdOn',
      key: 'createdOn',
      width: 130,
      sorter: true,
      render: text => <span>{text}</span>,
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      sorter: true,
      render: status => {
        return <StatusTag status={status} />;
      },
    },
    ...(can('edit') || can('delete')
      ? [
          {
            title: 'Actions',
            key: 'actions',
            align: 'center',
            width: 150,
            render: (_, record) => (
              <AIJDVTableActionWrapper>
                {can('edit') && (
                  <ThemeFilterBtn
                    shape="circle"
                    size="medium"
                    icon={<img src="/images/edit-icon.svg" alt="Edit" />}
                    onClick={() => handleEdit(record)}
                  />
                )}
                <Dropdown
            trigger={['click']}
            placement="bottomRight"
            dropdownRender={() => renderCardMenu(record)}
          >
                  <ThemeFilterBtn
                    shape="circle"
                    size="medium"
                    icon={<img src="/images/menu-dots-vertical.svg" alt="More" />}
                    onClick={(e) => e.stopPropagation()}
                  />
                </Dropdown>
              </AIJDVTableActionWrapper>
            ),
          },
        ]
      : []),
  ];

  return (
    <>
      <MobHeader />
      <ThemeMainWrapper>
          <Row gutter={[16, 16]} className="mb-6" align="middle">
            <Col
              xs={{ span: 8, order: 1 }}
              sm={{ span: 8, order: 1 }}
              md={{ span: 6, order: 1 }}
              lg={6}
              xl={8}
            >
              <GreetingText>Design</GreetingText>
            </Col>
            <Col
              xs={{ span: 24, order: 3 }}
              sm={{ span: 24, order: 3 }}
              md={{ span: 8, order: 1 }}
              lg={8}
              xl={8}
              className="text-center sm:text-center md:text-end lg:text-center xl:text-center"
            >
              <ThemeProjectTabWrapper>
                <div className="view-toggle">
                  <button
                    className={`toggle-btn ${
                      activeView === "card" ? "active" : ""
                    }`}
                    onClick={() => setActiveView("card")}
                  >
                    Card View
                  </button>
                  <button
                    className={`toggle-btn ${
                      activeView === "table" ? "active" : ""
                    }`}
                    onClick={() => setActiveView("table")}
                  >
                    Table View
                  </button>
                </div>
              </ThemeProjectTabWrapper>
            </Col>
            <Col
              xs={{ span: 16, order: 2 }}
              sm={{ span: 16, order: 2 }}
              md={{ span: 10, order: 1 }}
              lg={10}
              xl={8}
              className="text-end"
            >
              <Space gap={10}>
                {can('create') && (
                  isMobile ? (
                    <>
                      {!currentFolderId && (
                        <ThemeFilterBtn
                          className="background"
                          size="large"
                          shape="square"
                          icon={<img src="/images/folder-open-purple.svg" alt="Folder" style={{ width: 18, height: 18 }} />}
                          onClick={() => setCreateFolderModal(true)}
                        />
                      )}
                      <Link to={getCreateAgentUrl()}>
                        <ThemeFilterBtn
                          className="background"
                          size="large"
                          shape="square"
                          icon={<img src="/images/plus-icon.svg" alt="New AI JDV Agent" />}
                        />
                      </Link>
                    </>
                  ) : (
                    <>
                      {!currentFolderId && (
                        <NewFolderButton isMobile={false} onClick={() => setCreateFolderModal(true)} />
                      )}
                      <Link to={getCreateAgentUrl()}>
                        <ThemeButton
                          size="large"
                          icon={<img src="/images/plus-icon.svg" alt="New AI JDV Agent" />}
                        >
                          New AI JDV Agent
                        </ThemeButton>
                      </Link>
                    </>
                  )
                )}
              </Space>
            </Col>
          </Row>
          {currentFolderId && (
            <FolderBackNav currentFolderName={currentFolderName} onBack={handleBackToRoot} />
          )}
          <Row gutter={[16, 16]} className="mb-6" align="middle">
           <Col xs={24} sm={12} md={12} lg={10} xl={6} xxl={4}>
              <ThemeInput
                className="shadow"
                placeholder="Search AI JDV agents..."
                size="large"
                shape="square"
                prefix={<SearchOutlined />}
                value={searchValue}
                onChange={(e) => {
                  const value = e.target.value;
                  setSearchValue(value);
                  if (!value) {
                    handleSearch('');
                  }
                }}
                onPressEnter={(e) => handleSearch(e.target.value)}
              />
            </Col>
          </Row>
          {activeView === "card" && (
            <>
              {/* Folder section (only at root, not inside a folder) */}
              {!currentFolderId && (
                <>
                  <FolderCardsSection
                    filteredFolders={filteredFolders}
                    foldersCollapsed={foldersCollapsed}
                    setFoldersCollapsed={setFoldersCollapsed}
                    onFolderClick={handleFolderClick}
                    onRenameFolderClick={openRenameFolderModal}
                    onDeleteFolderClick={openDeleteFolderModal}
                    enableDrag={true}
                    dragOverFolderId={dragOverFolderId}
                    onDragOver={handleFolderDragOver}
                    onDragEnter={handleFolderDragEnter}
                    onDragLeave={handleFolderDragLeave}
                    onDrop={handleFolderDrop}
                  />
                  {filteredFolders.length > 0 && cardData.length > 0 && (
                    <ItemsSectionLabel label="AI JDV Agents" itemsCollapsed={agentsCollapsed} setItemsCollapsed={setAgentsCollapsed} />
                  )}
                </>
              )}
              {!(agentsCollapsed && filteredFolders.length > 0 && !currentFolderId) && (
              <>
              <Row gutter={[16, 16]} className="mb-6">
                {getPollingAgentsState.apiState === 'loading' ? (
                  // Show skeleton loading cards using ANTD Card's built-in loading prop
                  Array.from({ length: 6 }).map((_, index) => (
                    <Col span={24} sm={12} md={12} lg={12} xl={8} xxl={6} xxxl={4} key={`skeleton-${index}`}>
                      <AIJDVCard loading={true} />
                    </Col>
                  ))
                ) : cardData.length > 0 ? (
                  cardData.map((item) => (
                    <Col
                      span={24} sm={12} md={12} lg={12} xl={8} xxl={6} xxxl={4} key={item.key}
                      draggable={!currentFolderId && filteredFolders.length > 0}
                      onDragStart={!currentFolderId && filteredFolders.length > 0 ? (e) => handleDragStart(e, item) : undefined}
                      onDragEnd={!currentFolderId && filteredFolders.length > 0 ? handleDragEnd : undefined}
                      style={!currentFolderId && filteredFolders.length > 0 ? { cursor: 'grab' } : undefined}
                    >
                      <AIJDVCard onClick={() => navigate(getAgentDetailUrl(item.id))}>
                        <AIJDVCardInner>
                          <AIJDVCardHeader>
                            <AIJDVCardTitle>
                              {item.agentName}
                            </AIJDVCardTitle>
                            <Dropdown
                              trigger={['click']}
                              placement="bottomRight"
                              dropdownRender={() => renderCardMenu(item)}
                            >
                              <AIJDVCardMenuIcon onClick={(e) => e.stopPropagation()}>
                                <img src="/images/menu-dots-vertical.svg" alt="Menu" />
                              </AIJDVCardMenuIcon>
                            </Dropdown>
                          </AIJDVCardHeader>
                          <AIJDVCardDuration className="mb-1">
                            <img src="/images/clock-icon.svg" alt="Duration" />
                            <span>{item.maxDuration}</span>
                          </AIJDVCardDuration>
                          <AIJDVCardFooter>
                            <StatusTag status={item.status} />
                            <AIJDVCardDate>
                              <img src="/images/calendar-icon.svg" alt="Calendar" />
                              <span>Created - {item.createdOn}</span>
                            </AIJDVCardDate>
                          </AIJDVCardFooter>
                        </AIJDVCardInner>
                      </AIJDVCard>
                    </Col>
                  ))
                ) : (
                  <Col span={24} className="text-center">
                    <p>{currentFolderId ? 'No agents in this folder' : 'No AI JDV agents found'}</p>
                  </Col>
                )}
              </Row>
              {totalRecords > 0 && (
                <CardPaginationWrapper>
                  <Pagination
                    current={parseInt(filters.page) || 1}
                    pageSize={parseInt(filters.limit) || 12}
                    total={totalRecords}
                    showSizeChanger={true}
                    showTotal={(total, range) =>
                      `${range[0]}-${range[1]} of ${total} agents`
                    }
                    onChange={(page, pageSize) => handleTableChange({ current: page, pageSize }, null, {})}
                  />
                </CardPaginationWrapper>
              )}
              </>
              )}
            </>
          )}
          {activeView === "table" && (
            <>
              {/* Folder navigation for table view */}
              {!currentFolderId && (
                <>
                  <FolderCardsSection
                    filteredFolders={filteredFolders}
                    foldersCollapsed={foldersCollapsed}
                    setFoldersCollapsed={setFoldersCollapsed}
                    onFolderClick={handleFolderClick}
                    onRenameFolderClick={openRenameFolderModal}
                    onDeleteFolderClick={openDeleteFolderModal}
                  />
                  {filteredFolders.length > 0 && (
                    <ItemsSectionLabel label="AI JDV Agents" itemsCollapsed={agentsCollapsed} setItemsCollapsed={setAgentsCollapsed} />
                  )}
                </>
              )}
              {!(agentsCollapsed && filteredFolders.length > 0 && !currentFolderId) && (
              <ThemeTableCard>
                <Row gutter={[16, 16]}>
                  <Col xs={24}>
                    <ThemeTable
                      columns={columns}
                      dataSource={tableData}
                      pagination={{
                        current: parseInt(filters.page) || 1,
                        pageSize: parseInt(filters.limit) || 12,
                        total: totalRecords,
                        showSizeChanger: true,
                        showTotal: (total, range) =>
                          `${range[0]}-${range[1]} of ${total} agents`,
                      }}
                      onChange={handleTableChange}
                      loading={getPollingAgentsState.apiState === 'loading'}
                      scroll={{ x: 768 }}
                    />
                  </Col>
                </Row>
              </ThemeTableCard>
              )}
            </>
          )}
      </ThemeMainWrapper>

      {/* Delete Modal */}
      <ThemeModal
        title="Delete AI JDV Agent"
        open={deleteModalVisible}
        onOk={confirmDelete}
        onCancel={() => {
          setDeleteModalVisible(false);
          setSelectedAgent(null);
        }}
        confirmLoading={deletePollingAgentState.apiState === 'loading'}
        okText="Delete"
        cancelText="Cancel"
        centered
      >
        <p>
          {`Are you sure you want to delete AI JDV agent "${selectedAgent?.agentName}"?`}
        </p>
        <p>This action cannot be undone.</p>
      </ThemeModal>

      {/* Folder Modals (Create, Rename, Delete, Move, Remove) */}
      <FolderModals
        createFolderModal={createFolderModal}
        setCreateFolderModal={setCreateFolderModal}
        folderForm={folderForm}
        handleCreateFolder={handleCreateFolder}
        createFolderState={createFolderState}
        renameFolderModal={renameFolderModal}
        setRenameFolderModal={setRenameFolderModal}
        folderToRename={folderToRename}
        renameFolderForm={renameFolderForm}
        handleRenameFolder={handleRenameFolder}
        renameFolderState={renameFolderState}
        deleteFolderModal={deleteFolderModal}
        setDeleteFolderModal={setDeleteFolderModal}
        folderToDelete={folderToDelete}
        handleDeleteFolder={handleDeleteFolder}
        deleteFolderState={deleteFolderState}
        removeFromFolderModal={removeFromFolderModal}
        setRemoveFromFolderModal={setRemoveFromFolderModal}
        itemToRemove={agentToRemove}
        confirmRemoveFromFolder={confirmRemoveFromFolder}
        removeItemFromFolderState={removeItemFromFolderState}
        moveToFolderModal={moveToFolderModal}
        setMoveToFolderModal={setMoveToFolderModal}
        itemToMove={folderHook.itemToMove}
        selectedFolderId={selectedFolderId}
        setSelectedFolderId={setSelectedFolderId}
        handleMoveToFolder={handleMoveToFolder}
        addItemToFolderState={addItemToFolderState}
        folders={folders}
        currentFolderId={currentFolderId}
        itemLabel="agent"
      />
    </>
  );
};

const mapStateToProps = state => ({
  getPollingAgentsState: state.pollingAgent.getPollingAgentsState,
  deletePollingAgentState: state.pollingAgent.deletePollingAgentState,
});

const mapDispatchToProps = dispatch => ({
  getPollingAgents: params => dispatch(getPollingAgentsApi(params)),
  getPollingAgentsReset: () => dispatch(getPollingAgentsReset()),
  deletePollingAgent: params => dispatch(deletePollingAgentApi(params.id)),
  deletePollingAgentReset: () => dispatch(deletePollingAgentReset()),
});

export default connect(mapStateToProps, mapDispatchToProps)(PollAgents);
